import { useLazyUser } from "$global-providers/user-provider";
import { Role } from "elephant-web/gql";
import { useEffect, useState } from "react";
import type { RxCollection } from "rxdb";
import type { Price } from "../collections/price";
import { useRxCollectionPrice } from "../collections/price";
import type { PriceAcknowledgement } from "../collections/price-acknowledgement";
import { useRxCollectionPriceAcknowledgement } from "../collections/price-acknowledgement";
import type { PriceListFacility } from "../collections/price-list-facility";
import { useRxCollectionPriceListFacility } from "../collections/price-list-facility";

export type ChangedOrgPricesScan = {
  count: number;
  maxUpdatedAt: string | null;
  /**
   * Stays true until the first RxDB emission for the *current* inputs, so
   * callers don't act on a default `count: 0` before the scan settles, nor on
   * the previous inputs' output mid-transition.
   */
  loading: boolean;
  /** The `priceListId` that produced `count` / `maxUpdatedAt`. */
  scannedPriceListId: string | null;
  /** The `acknowledgedAt` cutoff that produced `count` / `maxUpdatedAt`. */
  scannedAcknowledgedAt: string | null;
};

export type ChangedOrgPrices = {
  /** Only facility admins review organisation-pushed price changes. */
  isFacilityAdmin: boolean;
  /** The facility's own price list — the one the review screen renders. */
  facilityPriceListId: string | null;
  /** The linked organisation list whose prices we scan for changes. */
  linkedOrgPriceListId: string | null;
  /** Caller's last acknowledgement timestamp, ISO string, or null. */
  acknowledgedAt: string | null;
  scan: ChangedOrgPricesScan;
};

/**
 * Single source of truth for "organisation prices changed on the facility's
 * linked org price-list since the user last reviewed". Consumed by both the
 * org-push notification adapter and the price-list review banner so their
 * counts can never diverge.
 */
export const useChangedOrgPrices = (): ChangedOrgPrices => {
  const user = useLazyUser();
  // Only facility admins review org-pushed changes. Every user carries a
  // facilityId, so without this gate an org admin would scan + surface a
  // facility-review banner on the organisation price list. Short-circuiting the
  // facility lookup here keeps the scan a no-op (count 0) for everyone else and
  // avoids needless RxDB subscriptions.
  const isFacilityAdmin = user?.role === Role.Clinic;

  const priceListFacilityCollection = useRxCollectionPriceListFacility();
  const priceAcknowledgementCollection = useRxCollectionPriceAcknowledgement();
  const priceCollection = useRxCollectionPrice();

  const facilityRow = useFacilityPriceListFacility(
    priceListFacilityCollection,
    isFacilityAdmin ? user?.facilityId : undefined,
  );
  const myAck = useUserPriceAcknowledgement(
    priceAcknowledgementCollection,
    user?.id,
  );

  const acknowledgedAt = myAck?.acknowledgedAt ?? null;
  const linkedOrgPriceListId =
    facilityRow?.linkedOrganisationPriceListId ?? null;
  const scan = useChangedPricesScan(
    priceCollection,
    linkedOrgPriceListId,
    acknowledgedAt,
  );

  return {
    isFacilityAdmin,
    facilityPriceListId: facilityRow?.priceListId ?? null,
    linkedOrgPriceListId,
    acknowledgedAt,
    scan,
  };
};

const useFacilityPriceListFacility = (
  collection: RxCollection<PriceListFacility> | null,
  facilityId: string | undefined,
): PriceListFacility | null => {
  // State carries both the row and the facilityId that produced it so a
  // user/facility switch can't expose the previous subscription's row in the
  // render before `useEffect` runs. The hook returns null until the new
  // subscription emits with a matching facilityId.
  const [state, setState] = useState<{
    row: PriceListFacility | null;
    forFacilityId: string | undefined;
  }>({ row: null, forFacilityId: undefined });
  useEffect(() => {
    if (!collection || !facilityId) {
      setState({ row: null, forFacilityId: facilityId });
      return;
    }
    const sub = collection
      .findOne({ selector: { id: facilityId } })
      .$.subscribe((doc) =>
        setState({
          row: doc ? (doc.toJSON() as PriceListFacility) : null,
          forFacilityId: facilityId,
        }),
      );
    return () => sub.unsubscribe();
  }, [collection, facilityId]);
  return state.forFacilityId === facilityId ? state.row : null;
};

const useUserPriceAcknowledgement = (
  collection: RxCollection<PriceAcknowledgement> | null,
  userId: string | undefined,
): PriceAcknowledgement | null => {
  const [state, setState] = useState<{
    row: PriceAcknowledgement | null;
    forUserId: string | undefined;
  }>({ row: null, forUserId: undefined });
  useEffect(() => {
    if (!collection || !userId) {
      setState({ row: null, forUserId: userId });
      return;
    }
    const sub = collection
      .findOne({ selector: { userId } })
      .$.subscribe((doc) =>
        setState({
          row: doc ? (doc.toJSON() as PriceAcknowledgement) : null,
          forUserId: userId,
        }),
      );
    return () => sub.unsubscribe();
  }, [collection, userId]);
  return state.forUserId === userId ? state.row : null;
};

const useChangedPricesScan = (
  collection: RxCollection<Price> | null,
  priceListId: string | null,
  acknowledgedAt: string | null,
): ChangedOrgPricesScan => {
  const [state, setState] = useState<ChangedOrgPricesScan>({
    count: 0,
    maxUpdatedAt: null,
    loading: true,
    scannedPriceListId: null,
    scannedAcknowledgedAt: null,
  });
  useEffect(() => {
    if (!collection || !priceListId) {
      setState({
        count: 0,
        maxUpdatedAt: null,
        loading: false,
        scannedPriceListId: priceListId,
        scannedAcknowledgedAt: acknowledgedAt,
      });
      return;
    }
    // No ack yet ⇒ first-visit user. Treat as a starting-clock visit with a
    // count of 0 so we don't flag every existing org price as "changed".
    if (acknowledgedAt === null) {
      setState({
        count: 0,
        maxUpdatedAt: null,
        loading: false,
        scannedPriceListId: priceListId,
        scannedAcknowledgedAt: null,
      });
      return;
    }
    setState((s) => ({
      ...s,
      loading: true,
      scannedPriceListId: priceListId,
      scannedAcknowledgedAt: acknowledgedAt,
    }));
    const sub = collection
      .find({
        selector: {
          priceListId,
          "meta.updatedAt": { $gt: acknowledgedAt },
        },
      })
      .$.subscribe((docs) => {
        let max: string | null = null;
        for (const d of docs) {
          const u = d.meta.updatedAt;
          if (!max || u > max) max = u;
        }
        setState({
          count: docs.length,
          maxUpdatedAt: max,
          loading: false,
          scannedPriceListId: priceListId,
          scannedAcknowledgedAt: acknowledgedAt,
        });
      });
    return () => sub.unsubscribe();
  }, [collection, priceListId, acknowledgedAt]);
  return state;
};
